import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addRsd } from '../../../store/slices/databaseSlice';
import { rsdsService } from '../../../services';
import { InverterBrand, Rsd } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface RsdForm {
  inverterBrandId: string;
  name: string;
}

export const RsdManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);

  const [form, setForm] = useState<RsdForm>({ inverterBrandId: '', name: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localRsds, setLocalRsds] = useState<Rsd[]>([]);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{ id: string; label: string } | null>(null);

  useEffect(() => {
    setLocalRsds(dbData.rsds);
  }, [dbData.rsds]);

  // Only show TESLA brand for now
  const brandOptions = dbData.inverter_brands
    .filter((brand: InverterBrand) => brand.name.toUpperCase() === 'TESLA')
    .map((brand: InverterBrand) => ({
      id: brand.id,
      label: brand.name,
    }));

  const handleBrandSelect = (option: { id: string; label: string } | null) => {
    setSelectedBrandOption(option);
    setForm((prev) => ({ ...prev, inverterBrandId: option?.id || '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.inverterBrandId) {
      toast('Please select an inverter brand', TOAST_WARNING_CONFIG);
      return;
    }
    if (!form.name.trim()) {
      toast('Please enter an RSD type name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newRsd = await rsdsService.create({
        name: form.name.trim(),
        inverter_brand_id: form.inverterBrandId,
      });

      dispatch(addRsd(newRsd));
      setLocalRsds((prev) => [...prev, newRsd]);
      setForm({ inverterBrandId: '', name: '' });
      setSelectedBrandOption(null);

      toast.success('RSD type created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating RSD:', error);
      toast.error('Failed to create RSD type. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group RSDs by brand for display
  const rsdsByBrand = localRsds.reduce<Record<string, Rsd[]>>((acc, rsd) => {
    const brandName = rsd.inverter_brand?.name || 'Unknown';
    if (!acc[brandName]) {
      acc[brandName] = [];
    }
    acc[brandName].push(rsd);
    return acc;
  }, {});

  return (
    <div className="p-4">
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">Add New RSD Type</h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Inverter Brand *</label>
              <SearchDropdown
                options={brandOptions}
                onSelect={handleBrandSelect}
                placeholder="Select inverter brand..."
                value={selectedBrandOption}
                className="text-sm"
                inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                zIndex={1000}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">RSD Type Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., MCI-2"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !form.inverterBrandId || !form.name.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create RSD Type'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">Existing RSD Types</h3>
            <span className="text-xs text-slate-500">{localRsds.length} items</span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {Object.keys(rsdsByBrand).length > 0 ? (
              <div className="space-y-3 p-2">
                {Object.entries(rsdsByBrand).map(([brandName, rsds]) => (
                  <div key={brandName}>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      {brandName}
                    </div>
                    <div className="space-y-1.5">
                      {rsds.map((rsd) => (
                        <div
                          key={rsd.id}
                          className="flex items-center justify-between p-2.5 bg-violet-50 text-violet-800 rounded-lg border border-violet-200 hover:bg-violet-100 transition-colors"
                        >
                          <span className="text-sm font-medium">{rsd.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">No RSD types found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
